import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useController } from "react-hook-form";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import i18next from "i18next";
import { useLocation } from "react-router-dom";
import { ref } from "firebase/database";
import { listenDatabaseValue as onValue } from "../../utils/listenDatabaseValue";
import { db } from "../../lib/firebase";
import {
  BUTTON_CONNECT_ACCOUNT,
  GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SETTINGS,
  FIELD_REQUIRED,
  LINKEDIN_PUBLISH_AS_PERSONAL_LABEL,
  LINKEDIN_PUBLISH_AS_COMPANY_PAGE_LABEL,
  TIKTOK_CREATE_AD_ACCOUNT_OPTION,
  AD_ACCOUNT_CREATED_FROM_BUSINESS,
} from "../../i18n/keysTranslations";
import { getReactHookFormNestedError } from "../../utils/errors";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startSyncAdsAccountsEntities } from "../../actions/user";
import { getNameBusinessTheme } from "../../actions/getters";
import {
  ADS_INTEGRATION_ENTITIES,
  GOOGLE,
  META,
  TIKTOK,
  LINKEDIN,
  LINKEDIN_PUBLISH_AS,
  IS_WHITE_LABEL_BRAND,
} from "../../utils/constants";
import { useTranslationApp } from "../../lib/i18next";
import ChipOptional from "../Chip/ChipOptional";
import AddIcon from "@mui/icons-material/Add";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import IconButton from "@mui/material/IconButton";
import GoogleAdsAccountExclusionsModal from "../Google/AccountExclusions/GoogleAdsAccountExclusionsModal";
import ModalCreateAdAccountTikTokBusiness from "../Modal/ModalCreateAdAccountTikTokBusiness";
import TikTokCreditBanner from "../TikTok/TikTokCreditBanner";
import TikTokCreditBadge from "../TikTok/TikTokCreditBadge";
import useButtonActionPlatform from "../../hooks/useButtonActionPlatform";
import { getUniqueID } from "../../utils/numbers";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import { getLinkedInPageAccountsForAdvertiser } from "../../services/linkedin";

const filter = createFilterOptions();
const ID_ADD = "Add Account";
const ID_CREATE_AD_ACCOUNT = "Create Ad Account";

const stopAutocompleteEvent = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const toExclusionsAccount = (account) => ({
  integrationID: account.integrationID,
  adAccountID: account.id,
  accountName: account.name || account.id,
});

const AccountExclusionsSettingsButton = ({ label, onOpen, sx }) => (
  <IconButton
    size="small"
    aria-label={label}
    sx={sx}
    onMouseDown={stopAutocompleteEvent}
    onClick={(event) => {
      stopAutocompleteEvent(event);
      onOpen();
    }}
  >
    <SettingsOutlinedIcon fontSize="small" />
  </IconButton>
);

const composeEndAdornment = (popupAdornment, leadingAdornments) => {
  const extras = leadingAdornments.filter(Boolean);
  if (extras.length === 0) return popupAdornment;
  if (!popupAdornment) return <>{extras}</>;

  return React.cloneElement(
    popupAdornment,
    {},
    ...extras,
    popupAdornment.props.children
  );
};

const extractLinkedInOrgIdFromReference = (reference = "") => {
  if (!reference) return "";
  const match = reference.match(/urn:li:organization:(.+)/);
  return match ? match[1] : "";
};

const buildLinkedInOrganizationOptionsFromAdvertisers = ({
  integration,
  advertisers = [],
}) => {
  const groupedAdvertisers = new Map();

  advertisers.forEach((advertiser) => {
    const reference = advertiser.reference || "";
    const pageName = advertiser.pageName || "";
    const key = `${reference}-${pageName}`;

    if (!groupedAdvertisers.has(key)) {
      groupedAdvertisers.set(key, []);
    }
    groupedAdvertisers.get(key).push(advertiser);
  });

  const organizationAccounts = [];

  groupedAdvertisers.forEach((advertiserGroup) => {
    const firstAdvertiser = advertiserGroup[0];
    const reference = firstAdvertiser.reference || "";
    const orgUrn = extractLinkedInOrgIdFromReference(reference);
    const pageName = firstAdvertiser.pageName || "";
    const displayName = pageName || orgUrn;

    organizationAccounts.push({
      id: firstAdvertiser.id,
      name: pageName || orgUrn,
      integrationID: integration.id,
      publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
      orgUrn,
      displayLabel: displayName,
      advertiserIds: advertiserGroup.map((adv) => adv.id),
    });
  });

  return organizationAccounts;
};

const SelectBusinessAccount = ({
  control,
  errors = {},
  rules = {
    required: {
      value: true,
      message: i18next.t(FIELD_REQUIRED),
    },
  },
  name,
  label,
  disabled = false,
  placeholder,
  variant = "filled",
  platform,
  fullWidth,
  helperText,
  onChangeAction = () => {},
  validationChange = async () => true,
  sxTextField = {},
  linkedInForPublishing = false,
  linkedInCompanyPagesOnly = false,
  linkedInRestrictToCampaignAdvertiserPages = false,
  linkedInCampaignIntegrationID = "",
  linkedInCampaignAdvertiserID = "",
  componentsProps = {},
  disableSyncEntities = false,
  disableAutoSelect = false,
  showAccountExclusionsAction = false,
  disableCreateTikTokAdAccount = false,
  hideTikTokAdAccountCreditBanner = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [id] = useState(getUniqueID());
  const [connectingAccount, setConnectingAccount] = useState(false);
  const [isUpdatingEntities, setIsUpdatingEntities] = useState(false);
  const [exclusionsAccount, setExclusionsAccount] = useState(null);
  const [openCreateAdAccountModal, setOpenCreateAdAccountModal] =
    useState(false);
  const [partnerAdAccountIDs, setPartnerAdAccountIDs] = useState({});
  const { pathname } = useLocation();
  const isCampaignView = pathname.replace(/\/+$/, "") === "/ads-campaign";
  const optionsKeyRef = useRef("");
  const connectKeepAutocompleteClosedRef = useRef(false);
  const skipConnectChoiceRef = useRef(false);
  const canOpenAccountExclusions =
    showAccountExclusionsAction && platform === GOOGLE;

  const {
    field: { value: fieldValue, onChange: onFieldChange },
  } = useController({
    name,
    control,
  });
  const error = getReactHookFormNestedError(errors, name);
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const BUTTON_ACTIONS_COMPONENTS = useButtonActionPlatform();

  const { isLoading, integrations } = useSelectorApp((state) => state.user);
  const metaIntegrations = integrations?.facebook;
  const metaIntegrationIdsKey = useMemo(
    () =>
      Object.keys(metaIntegrations || {})
        .sort()
        .join(","),
    [metaIntegrations]
  );
  const tikTokBusinessIntegrations = integrations?.tiktokBusiness;
  const tikTokBusinessIntegrationIDs = useMemo(
    () => Object.keys(tikTokBusinessIntegrations || {}),
    [tikTokBusinessIntegrations]
  );
  const canCreateTikTokAdAccount =
    platform === TIKTOK &&
    !IS_WHITE_LABEL_BRAND &&
    !disableCreateTikTokAdAccount &&
    tikTokBusinessIntegrationIDs.length > 0;
  const googleIntegrations = integrations?.google;
  const linkedInIntegrations = integrations?.linkedIn;
  const linkedInIntegrationIdsKey = useMemo(
    () =>
      Object.keys(linkedInIntegrations || {})
        .sort()
        .join(","),
    [linkedInIntegrations]
  );

  useEffect(() => {
    if (!platform) {
      return undefined;
    }

    const unsubscribe = onValue(
      ref(db, `adAccountsPartners/${platform}`),
      (snapshot) => {
        setPartnerAdAccountIDs(snapshot.val() || {});
      }
    );

    return unsubscribe;
  }, [platform]);

  const onClickConnectAccount = async ({
    keepAutocompleteClosed = false,
  } = {}) => {
    connectKeepAutocompleteClosedRef.current = keepAutocompleteClosed;
    skipConnectChoiceRef.current = keepAutocompleteClosed;
    if (keepAutocompleteClosed) {
      setOpen(false);
    }
    setConnectingAccount(true);
    const element = document.getElementById(id);
    element?.click();
    return;
  };

  useEffect(() => {
    if (platform !== META || !metaIntegrationIdsKey) {
      return;
    }
    const integrationIDs = metaIntegrationIdsKey.split(",").filter(Boolean);
    setIsUpdatingEntities(true);
    Promise.all(
      integrationIDs.map((fbIntegrationID) =>
        dispatch(
          startSyncAdsAccountsEntities({
            integrationID: fbIntegrationID,
            platform: META,
            entity: ADS_INTEGRATION_ENTITIES.AD_ACCOUNTS,
          })
        )
      )
    ).finally(() => {
      setIsUpdatingEntities(false);
    });
  }, [
    platform,
    metaIntegrationIdsKey,
    connectingAccount,
    dispatch,
    disableSyncEntities,
  ]);

  useEffect(() => {
    if (
      platform !== LINKEDIN ||
      !linkedInIntegrationIdsKey ||
      disableSyncEntities
    ) {
      return;
    }
    const integrationIDs = linkedInIntegrationIdsKey.split(",").filter(Boolean);
    setIsUpdatingEntities(true);
    Promise.all(
      integrationIDs.map((linkedInIntegrationID) =>
        dispatch(
          startSyncAdsAccountsEntities({
            integrationID: linkedInIntegrationID,
            platform: LINKEDIN,
            entity: ADS_INTEGRATION_ENTITIES.AD_ACCOUNTS,
          })
        )
      )
    ).finally(() => {
      setIsUpdatingEntities(false);
    });
  }, [
    platform,
    linkedInIntegrationIdsKey,
    connectingAccount,
    dispatch,
    disableSyncEntities,
  ]);

  useEffect(() => {
    if (platform !== META) {
      // Evita que la comparación de Meta se salte setOptions al volver a Meta
      // después de haber mostrado las opciones de otra plataforma
      optionsKeyRef.current = "";
    }
    if (platform === META) {
      const businessAccount = Object.keys(metaIntegrations || {}).map((key) => {
        const integration = metaIntegrations[key];
        const { businessAccounts, name } = integration;
        const businessAccountsKeys = Object.keys(businessAccounts);
        return businessAccountsKeys.map((key) => ({
          ...businessAccounts[key],
          integrationID: integration.id,
          accountOwner: name,
        }));
      });
      const flatOptions = businessAccount.flat();
      const nextOptionsKey = flatOptions.map((o) => String(o.id)).join("|");
      if (nextOptionsKey !== optionsKeyRef.current) {
        optionsKeyRef.current = nextOptionsKey;
        setOptions(flatOptions);
      }
    }

    if (platform === TIKTOK) {
      const businessAccount = Object.keys(tikTokBusinessIntegrations || {}).map(
        (key) => {
          const integration = tikTokBusinessIntegrations[key];
          const { advertisers = {} } = integration;
          const advertisersKeys = Object.keys(advertisers);
          return advertisersKeys.map((advertiserKey) => {
            const advertiser = advertisers[advertiserKey];
            const adAccountID = String(advertiser?.id || advertiserKey);
            return {
              ...advertiser,
              integrationID: integration.id,
              createdFromBusiness: Boolean(partnerAdAccountIDs[adAccountID]),
            };
          });
        }
      );
      setOptions(businessAccount.flat());
    }

    if (platform === GOOGLE) {
      const businessAccount = Object.keys(googleIntegrations || {}).map(
        (key) => {
          const integration = googleIntegrations[key];
          const { name, advertisers } = integration;
          const advertisersKeys = Object.keys(advertisers);
          return advertisersKeys.map((key) => ({
            ...advertisers[key],
            integrationID: integration.id,
            accountOwner: name,
          }));
        }
      );
      setOptions(businessAccount.flat());
    }

    if (platform === LINKEDIN && !linkedInForPublishing) {
      const businessAccount = Object.keys(linkedInIntegrations || {}).map(
        (key) => {
          const integration = linkedInIntegrations[key];
          const { advertisers } = integration;
          const advertisersKeys = Object.keys(advertisers || {});
          return advertisersKeys.map((key) => ({
            ...advertisers[key],
            integrationID: integration.id,
          }));
        }
      );
      setOptions(businessAccount.flat());
    }

    if (platform === LINKEDIN && linkedInForPublishing) {
      if (
        linkedInRestrictToCampaignAdvertiserPages &&
        linkedInCampaignIntegrationID &&
        linkedInCampaignAdvertiserID
      ) {
        const integration =
          linkedInIntegrations?.[linkedInCampaignIntegrationID];
        setOptions(
          getLinkedInPageAccountsForAdvertiser({
            integration,
            adAccountID: linkedInCampaignAdvertiserID,
          })
        );
        return;
      }

      const publishingAccounts = Object.values(
        linkedInIntegrations || {}
      ).flatMap((integration) => {
        const accounts = [];

        if (!linkedInCompanyPagesOnly) {
          accounts.push({
            id: integration.id,
            name: integration?.name,
            integrationID: integration.id,
            publishAs: LINKEDIN_PUBLISH_AS.PROFILE,
            isPersonal: true,
            displayLabel: integration?.name || "",
          });
        }

        const pages = Object.values(integration?.pages || {});

        if (pages.length > 0) {
          pages.forEach((page) => {
            const orgId = String(page.id);
            const organizationUrn =
              page.organizationUrn || `urn:li:organization:${orgId}`;
            const orgUrn = extractLinkedInOrgIdFromReference(organizationUrn);
            const matchingAdvertisers = Object.values(
              integration?.advertisers || {}
            ).filter(
              (advertiser) =>
                advertiser?.reference === organizationUrn ||
                extractLinkedInOrgIdFromReference(advertiser?.reference) ===
                  orgId
            );

            accounts.push({
              id: orgId,
              name: page.name || orgId,
              integrationID: integration.id,
              publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
              orgUrn,
              organizationUrn,
              displayLabel: page.name || orgId,
              isPage: true,
              pageId: orgId,
              advertiserIds: matchingAdvertisers.map(
                (advertiser) => advertiser.id
              ),
            });
          });
        } else {
          accounts.push(
            ...buildLinkedInOrganizationOptionsFromAdvertisers({
              integration,
              advertisers: Object.values(integration?.advertisers || {}),
            })
          );
        }

        return accounts;
      });
      setOptions(publishingAccounts);
    }
  }, [
    metaIntegrations,
    tikTokBusinessIntegrations,
    googleIntegrations,
    linkedInIntegrations,
    platform,
    connectingAccount,
    linkedInForPublishing,
    linkedInCompanyPagesOnly,
    linkedInRestrictToCampaignAdvertiserPages,
    linkedInCampaignIntegrationID,
    linkedInCampaignAdvertiserID,
    partnerAdAccountIDs,
  ]);

  useEffect(() => {
    if (disableAutoSelect || isCampaignView) {
      return;
    }
    if (!fieldValue && options.length > 0) {
      const firstOption = options[0];
      onFieldChange(firstOption.id);
      onChangeAction(firstOption);
    }
    // eslint-disable-next-line
  }, [options, disableAutoSelect]);

  const isSelectedAccountCreatedFromBusiness =
    platform === TIKTOK &&
    Boolean(
      options.find((option) => option.id === fieldValue)?.createdFromBusiness
    );

  if (isLoading) return null;
  return (
    <>
      {React.cloneElement(BUTTON_ACTIONS_COMPONENTS[platform], {
        id,
        text: BUTTON_CONNECT_ACCOUNT,
        mode: "add",
        // Connecting from the create-ad-account modal goes straight to the guide.
        offerCreateAdAccount: () => {
          const skipChoice = skipConnectChoiceRef.current;
          skipConnectChoiceRef.current = false;
          return !skipChoice;
        },
        icon: <AddIcon />,
        showButtonIcon: true,
        showLogo: false,
        onClick: () => {
          setConnectingAccount(false);
          if (connectKeepAutocompleteClosedRef.current) {
            setOpen(false);
            connectKeepAutocompleteClosedRef.current = false;
            return;
          }
          setOpen(true);
        },
        onCallbackOpenPopup: () => {
          setConnectingAccount(false);
        },
        sx: {
          display: "none",
          justifyContent: "flex-start",
          background: "transparent",
          color: "inherit",
          m: 0,
          ml: -1,
          py: 1,
          px: 2,
          fontSize: "1rem",
          fontWeight: 500,
          "&:hover": {
            background: "transparent",
          },
        },
      })}
      {platform === TIKTOK &&
      !IS_WHITE_LABEL_BRAND &&
      !disableCreateTikTokAdAccount &&
      !hideTikTokAdAccountCreditBanner &&
      !isSelectedAccountCreatedFromBusiness ? (
        <TikTokCreditBanner
          nameBusiness={NAME_BUSINESS}
          onClick={() => {
            setOpen(false);
            setOpenCreateAdAccountModal(true);
          }}
        />
      ) : null}
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => (
          <Autocomplete
            {...field}
            componentsProps={componentsProps}
            loading={connectingAccount}
            hasLabel={Boolean(label)}
            id={name}
            value={options?.length > 0 ? field.value : ""}
            fullWidth={fullWidth}
            disabled={disabled}
            open={open}
            onOpen={() => {
              setOpen(true);
            }}
            onClose={() => {
              setOpen(false);
            }}
            onChange={async (_, value) => {
              const isValid = await validationChange(value);
              if (!isValid) {
                return;
              }
              if (value?.id === ID_ADD) {
                onClickConnectAccount();
                return;
              }
              if (value?.id === ID_CREATE_AD_ACCOUNT) {
                setOpen(false);
                setOpenCreateAdAccountModal(true);
                return;
              }

              field.onChange(value?.id);
              onChangeAction(value);
            }}
            isOptionEqualToValue={(option, value) => {
              if (value?.id) {
                return option?.id === value?.id;
              }
              return option?.id === value;
            }}
            disableClearable={true}
            getOptionLabel={(option) => {
              if (option == null || option === "") return "";
              const linkedInOptionLabel = (opt) =>
                opt?.displayLabel || opt?.name || "";

              if (platform === LINKEDIN) {
                const optionValue = options.find((opt) => opt.id === option);
                if (optionValue) {
                  return linkedInOptionLabel(optionValue);
                }
                if (typeof option === "object" && option !== null) {
                  return linkedInOptionLabel(option);
                }
                return typeof option === "string" ? option : "";
              }

              const optionValue = options.find((opt) => opt.id === option);
              if (optionValue) {
                if (optionValue.displayLabel) {
                  return optionValue.displayLabel;
                }
                return `${optionValue?.name} - ${optionValue?.id}`;
              }
              if (typeof option === "string") {
                return option;
              }
              if (option?.displayLabel) {
                return option.displayLabel;
              }
              return `${option?.name} - ${option?.id}`;
            }}
            options={options}
            filterOptions={(options, params) => {
              const filtered = filter(options, params);
              filtered.unshift({
                id: ID_ADD,
                name: t(BUTTON_CONNECT_ACCOUNT),
              });
              if (canCreateTikTokAdAccount) {
                filtered.unshift({
                  id: ID_CREATE_AD_ACCOUNT,
                  name: t(TIKTOK_CREATE_AD_ACCOUNT_OPTION),
                });
              }

              return filtered;
            }}
            renderOption={(props, option) => {
              const showPublishAsChip =
                linkedInForPublishing &&
                platform === LINKEDIN &&
                option.id !== ID_ADD &&
                (option.publishAs || option.isPersonal || option.isPage);

              const publishAsChipLabel = option.isPersonal
                ? t(LINKEDIN_PUBLISH_AS_PERSONAL_LABEL)
                : option.isPage ||
                    option.publishAs === LINKEDIN_PUBLISH_AS.ORGANIZATION
                  ? t(LINKEDIN_PUBLISH_AS_COMPANY_PAGE_LABEL)
                  : option.publishAs === LINKEDIN_PUBLISH_AS.PROFILE
                    ? t(LINKEDIN_PUBLISH_AS_PERSONAL_LABEL)
                    : null;

              return (
                <Box
                  {...props}
                  component="li"
                  key={`${option.id}-${option.integrationID || option.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      flex: 1,
                    }}
                  >
                    {option.id === ID_ADD && (
                      <AddIcon
                        sx={{
                          ml: -0.5,
                          color: "background.paperContrastText",
                        }}
                      />
                    )}
                    {option.id === ID_CREATE_AD_ACCOUNT && (
                      <PersonAddIcon
                        sx={{
                          ml: -0.5,
                          color: "background.paperContrastText",
                        }}
                      />
                    )}
                    <Typography
                      variant="body1"
                      noWrap
                      sx={{ color: "background.paperContrastText" }}
                    >
                      {option.displayLabel || option.name}
                      {option.accountOwner && ` (${option.accountOwner})`}
                    </Typography>
                  </Box>
                  {canOpenAccountExclusions && option.id !== ID_ADD && (
                    <AccountExclusionsSettingsButton
                      label={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SETTINGS)}
                      sx={{ flexShrink: 0 }}
                      onOpen={() => {
                        setOpen(false);
                        setExclusionsAccount(toExclusionsAccount(option));
                      }}
                    />
                  )}
                  {showPublishAsChip && publishAsChipLabel && (
                    <Chip
                      label={publishAsChipLabel}
                      size="small"
                      variant="outlined"
                      sx={{
                        flexShrink: 0,
                        height: 22,
                        fontSize: "0.7rem",
                      }}
                    />
                  )}
                  {option.createdFromBusiness && (
                    <ChipOptional
                      size="small"
                      label={t(AD_ACCOUNT_CREATED_FROM_BUSINESS, {
                        NAME_BUSINESS,
                      })}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
              );
            }}
            renderInput={(params) => {
              const selectedAccount = options.find(
                (option) => option.id === field.value
              );

              return (
                <TextField
                  {...params}
                  hiddenLabel={!label}
                  label={label}
                  variant={variant}
                  placeholder={field?.value?.length > 0 ? "" : placeholder}
                  error={!!error}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: isSelectedAccountCreatedFromBusiness ? (
                      <TikTokCreditBadge
                        nameBusiness={NAME_BUSINESS}
                        sx={{ mr: 0.75 }}
                      />
                    ) : (
                      params.InputProps.startAdornment
                    ),
                    endAdornment: composeEndAdornment(
                      params.InputProps.endAdornment,
                      [
                        (connectingAccount || isUpdatingEntities) && (
                          <CircularProgress color="inherit" size={20} />
                        ),
                        canOpenAccountExclusions && selectedAccount && (
                          <AccountExclusionsSettingsButton
                            label={t(GOOGLE_ADS_ACCOUNT_EXCLUSIONS_SETTINGS)}
                            sx={{ px: 0.25, pt: 0.5, pb: 0.25 }}
                            onOpen={() => {
                              setOpen(false);
                              setExclusionsAccount(
                                toExclusionsAccount(selectedAccount)
                              );
                            }}
                          />
                        ),
                      ]
                    ),
                  }}
                  sx={{
                    backgroundColor: "fields.background",
                    border: `1px solid ${INPUT_COLOR_BORDER}`,
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    ...sxTextField,
                    ".MuiFormHelperText-root": {
                      ml: "0 !important",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      display: "flex",
                      alignItems: "center",
                      top: "50%",
                      transform: "translateY(-50%)",
                    },
                  }}
                />
              );
            }}
          />
        )}
      />
      {(error?.message || helperText) && (
        <Typography
          variant="caption"
          color={error ? "error" : "text.secondary"}
          sx={{ display: "block", mt: 0.5 }}
        >
          {error ? error.message : helperText}
        </Typography>
      )}
      {platform === TIKTOK &&
      !IS_WHITE_LABEL_BRAND &&
      !disableCreateTikTokAdAccount &&
      openCreateAdAccountModal ? (
        <ModalCreateAdAccountTikTokBusiness
          modalOpen={openCreateAdAccountModal}
          integrationIDs={tikTokBusinessIntegrationIDs}
          onCloseModal={() => setOpenCreateAdAccountModal(false)}
          onConnectAccount={() =>
            onClickConnectAccount({ keepAutocompleteClosed: true })
          }
          onCallbackCreate={(advertiser, integrationID) => {
            onFieldChange(advertiser.id);
            onChangeAction({ ...advertiser, integrationID });
          }}
        />
      ) : null}
      {showAccountExclusionsAction ? (
        <GoogleAdsAccountExclusionsModal
          modalOpen={Boolean(exclusionsAccount)}
          onCloseModal={() => setExclusionsAccount(null)}
          integrationID={exclusionsAccount?.integrationID}
          adAccountID={exclusionsAccount?.adAccountID}
          accountName={exclusionsAccount?.accountName}
        />
      ) : null}
    </>
  );
};

export default SelectBusinessAccount;
